import { useState } from 'react';
import { enableImpersonationWriteMode } from '@halo/web-lib';

interface Props {
  storeName: string;
  adminEmail: string;
  onClose: () => void;
  onEnabled: () => void;
}

export function EnableWriteModeModal({ storeName, adminEmail, onClose, onEnabled }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setLoading(true);
    setError(null);
    try {
      await enableImpersonationWriteMode();
      onEnabled();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to enable write mode');
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="write-mode-modal-title"
    >
      <div className="w-[520px] rounded-lg bg-white p-6 shadow-xl">
        <h2 id="write-mode-modal-title" className="text-lg font-semibold">
          Enable write mode
        </h2>
        <p className="mt-3 text-sm text-gray-700">
          You're about to enable write mode for <strong>{storeName}</strong>. Any changes
          you make will be attributed to you (<strong>{adminEmail}</strong>) and logged.
          The following actions remain blocked even in write mode:
        </p>
        <ul className="mt-2 ml-5 list-disc text-sm text-gray-700">
          <li>Store deletion</li>
          <li>Ownership transfer</li>
          <li>Owner credential changes</li>
          <li>Subscription cancellation</li>
          <li>Payment method changes</li>
        </ul>
        {error && (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={loading}
            className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? 'Enabling…' : 'Enable write mode'}
          </button>
        </div>
      </div>
    </div>
  );
}
